'use client';

import { ChevronDown, Moon, Sun } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';

import { NAV_GIFT } from '@/lib/components/RewardsHub/navGift';
import { useThemeTransition } from '@/lib/hooks/useThemeTransition';
import { FadeInGroup, FadeInItem } from '@/lib/ui-kit/fade-in';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { type LanguageOption, LanguageRow } from '@/lib/ui-kit-v2/language-row';

type Theme = 'light' | 'dark';

interface UserMenuProps {
  theme?: Theme;
  onThemeChange?: (theme: Theme) => void;
  language?: string;
  flag?: React.ReactNode;
  onLanguageClick?: () => void;
  languageOptions?: LanguageOption[];
  currentLanguageCode?: string;
  onLanguageChange?: (code: string) => void;
  /** Shown below 1024px, where the header nav (and its Rewards Hub entry) is hidden. */
  onRewardsHubClick?: () => void;
  onLeaderboardClick?: () => void;
  onNewsClick?: () => void;
  onApiClick?: () => void;
  onSettingsClick?: () => void;
  onHelpCenterClick?: () => void;
  className?: string;
}

function UserMenu({
  theme = 'dark',
  onThemeChange,
  language = 'En',
  flag,
  onLanguageClick,
  languageOptions,
  currentLanguageCode,
  onLanguageChange,
  onRewardsHubClick,
  onLeaderboardClick,
  onNewsClick,
  onApiClick,
  onSettingsClick,
  onHelpCenterClick,
  className,
}: UserMenuProps) {
  const t = useTranslations('nav');
  return (
    <FadeInGroup
      staggerDelay={0.04}
      className={cn(
        'flex w-[240px] flex-col gap-4 rounded-[10px] border border-solid border-v2-border bg-v2-surface-secondary p-5',
        'shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]',
        className,
      )}
    >
      {onRewardsHubClick && (
        <FadeInItem yOffset={8} duration={0.2} className="min-[1024px]:hidden">
          <MenuRow onClick={onRewardsHubClick} compact>
            <img src={NAV_GIFT} alt="" className="size-4 shrink-0" draggable={false} />
            <span className="text-v2-text-primary group-hover:text-v2-bg-blue">
              {t('rewardsHub')}
            </span>
          </MenuRow>
        </FadeInItem>
      )}

      {onLeaderboardClick && (
        <FadeInItem yOffset={8} duration={0.2}>
          <MenuRow onClick={onLeaderboardClick} compact>
            <Icon
              name="Insight"
              size={16}
              className="shrink-0 text-v2-icon-primary group-hover:text-v2-bg-blue"
            />
            <span className="text-v2-text-primary group-hover:text-v2-bg-blue">
              {t('leaderboard')}
            </span>
          </MenuRow>
        </FadeInItem>
      )}

      {onNewsClick && (
        <FadeInItem yOffset={8} duration={0.2}>
          <MenuRow onClick={onNewsClick} compact>
            <Icon
              name="Newspaper"
              size={16}
              className="shrink-0 text-v2-icon-primary group-hover:text-v2-bg-blue"
            />
            <span className="text-v2-text-primary group-hover:text-v2-bg-blue">{t('news')}</span>
          </MenuRow>
        </FadeInItem>
      )}

      {onApiClick && (
        <FadeInItem yOffset={8} duration={0.2}>
          <MenuRow onClick={onApiClick} compact>
            <Icon
              name="Key2"
              size={16}
              className="shrink-0 text-v2-icon-primary group-hover:text-v2-bg-blue"
            />
            <span className="flex items-center gap-1 text-v2-text-primary group-hover:text-v2-bg-blue">
              {t('api')}
              <Icon
                name="Link2"
                size={12}
                className="shrink-0 text-v2-icon-primary group-hover:text-v2-bg-blue"
              />
            </span>
          </MenuRow>
        </FadeInItem>
      )}

      {onSettingsClick && (
        <FadeInItem yOffset={8} duration={0.2}>
          <MenuRow onClick={onSettingsClick} compact>
            <Icon
              name="Settings2"
              size={16}
              className="shrink-0 text-v2-icon-primary group-hover:text-v2-bg-blue"
            />
            <span className="text-v2-text-primary group-hover:text-v2-bg-blue">
              {t('settings')}
            </span>
          </MenuRow>
        </FadeInItem>
      )}

      <FadeInItem yOffset={8} duration={0.2}>
        <MenuRow onClick={onHelpCenterClick} compact>
          <Icon
            name="Hexagon"
            size={16}
            className="shrink-0 text-v2-icon-primary group-hover:text-v2-bg-blue"
          />
          <span className="text-v2-text-primary group-hover:text-v2-bg-blue">
            {t('helpCenter')}
          </span>
        </MenuRow>
      </FadeInItem>

      <FadeInItem yOffset={8} duration={0.2}>
        <div aria-hidden className="h-px w-full bg-v2-border" />
      </FadeInItem>

      <FadeInItem yOffset={8} duration={0.2}>
        <div className="flex w-full items-center gap-2 py-1">
          <span className="flex-1 text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-primary">
            {t('theme')}
          </span>
          <ThemeToggle />
        </div>
      </FadeInItem>

      <FadeInItem yOffset={8} duration={0.2}>
        {languageOptions && languageOptions.length > 0 && onLanguageChange ? (
          <LanguageRow
            options={languageOptions}
            selectedCode={currentLanguageCode ?? languageOptions[0]?.code ?? 'en'}
            onSelect={onLanguageChange}
            label={t('language')}
          />
        ) : (
          <button
            type="button"
            onClick={onLanguageClick}
            className="group flex w-full cursor-pointer items-center gap-2 py-1 outline-none"
          >
            <span className="flex-1 text-left text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-primary group-hover:text-v2-bg-blue">
              {t('language')}
            </span>
            <span className="flex shrink-0 items-center gap-1">
              <span className="h-3 w-[18px] shrink-0 overflow-hidden rounded-[2px] shadow-[0_1px_2px_rgba(0,0,0,0.15)] dark:shadow-none">
                {flag ?? <FlagUK />}
              </span>
              <span className="flex items-center gap-0.5 text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-primary">
                {language.toUpperCase()}
                <ChevronDown className="size-4 text-v2-icon-primary" strokeWidth={2} />
              </span>
            </span>
          </button>
        )}
      </FadeInItem>
    </FadeInGroup>
  );
}

function MenuRow({
  children,
  onClick,
  compact,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex cursor-pointer items-center gap-2 py-1 text-left outline-none',
        'text-[13px] font-normal leading-4 tracking-[-0.13px]',
        compact ? 'w-[120px]' : 'w-full',
      )}
    >
      {children}
    </button>
  );
}

type ThemeToggleSize = 'M' | 'L';

interface ThemeToggleProps {
  /** `M` — desktop user/account menu; `L` — burger-menu drawer. */
  size?: ThemeToggleSize;
}

interface ThemeButtonClasses {
  base: string;
  active: string;
  inactive: string;
}

const THEME_TOGGLE_CLASSES: Record<ThemeToggleSize, string> = {
  M: 'gap-1 border-v2-btn-secondary-2',
  L: 'overflow-clip border-v2-border p-0.5',
};

const THEME_BUTTON_CLASSES: Record<ThemeToggleSize, ThemeButtonClasses> = {
  M: {
    base: 'size-[22px] p-1 text-v2-text-primary',
    active: 'bg-v2-btn-secondary-2 opacity-100',
    inactive: 'opacity-50 hover:opacity-80',
  },
  L: {
    base: 'size-8',
    active: 'bg-v2-surface-secondary text-v2-text-primary',
    inactive: 'text-v2-text-secondary hover:text-v2-text-primary',
  },
};

const THEME_ICONS: Record<ThemeToggleSize, Record<Theme, React.ReactNode>> = {
  M: {
    light: <Sun className="size-[14px]" strokeWidth={1.6} />,
    dark: <Moon className="size-[14px]" strokeWidth={1.6} />,
  },
  L: {
    light: <Icon name="ThemeSun" size={18} />,
    dark: <Icon name="ThemeMoon" size={18} />,
  },
};

export function ThemeToggle({ size = 'M' }: ThemeToggleProps) {
  const { resolvedTheme, setThemeAnimated } = useThemeTransition();
  const isLight = resolvedTheme !== 'dark';

  const handleLightClick = () => setThemeAnimated('light');
  const handleDarkClick = () => setThemeAnimated('dark');

  return (
    <div
      className={cn(
        'flex items-center rounded-full border border-solid',
        THEME_TOGGLE_CLASSES[size],
      )}
    >
      <ThemeButton active={isLight} size={size} onClick={handleLightClick} aria-label="Light theme">
        {THEME_ICONS[size].light}
      </ThemeButton>
      <ThemeButton active={!isLight} size={size} onClick={handleDarkClick} aria-label="Dark theme">
        {THEME_ICONS[size].dark}
      </ThemeButton>
    </div>
  );
}

function ThemeButton({
  active,
  size = 'M',
  children,
  onClick,
  ...props
}: {
  active?: boolean;
  size?: ThemeToggleSize;
  children: React.ReactNode;
  onClick?: () => void;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children'>) {
  const classes = THEME_BUTTON_CLASSES[size];

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex cursor-pointer items-center justify-center rounded-full outline-none transition-all duration-150',
        classes.base,
        active ? classes.active : classes.inactive,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function FlagUK() {
  return (
    <svg viewBox="0 0 60 60" className="size-full" aria-hidden>
      <rect width="60" height="60" fill="#012169" />
      <path d="M0 0 L60 60 M60 0 L0 60" stroke="#FFFFFF" strokeWidth="12" />
      <path d="M0 0 L60 60 M60 0 L0 60" stroke="#C8102E" strokeWidth="6" />
      <path d="M30 0 V60 M0 30 H60" stroke="#FFFFFF" strokeWidth="20" />
      <path d="M30 0 V60 M0 30 H60" stroke="#C8102E" strokeWidth="10" />
    </svg>
  );
}

export type { UserMenuProps };
export { UserMenu };
